import { useContext, useEffect, useId, useState } from "react";
import apiClient from "../helpers/api-client";
import { handleAuthenticatedTabOpen } from "../helpers/open-authenticated";
import PortalAuthenticationManagerContext, { usePortalStateContext } from "../portal/context";
import { Box, Skeleton, Typography } from "@mui/material";
import RefreshReportModal from "../admin/report/components/RefreshReport";
import {
	CoreButton,
	CoreCard,
	CoreSearchField,
	CoreSearchFieldPaper,
	CoreTable,
	CoreTableColumn,
	coreToast,
	CoreToastContainer,
} from "@verity/components/core";
import CourseIcon from "../images/reports/courseIcon.svg?react";
import VisualizerBanner from "../portal/components/visualizer-banner";
import { createRoot } from "react-dom/client";
import createPortalAuthenticationManager from "../portal/portal-authentication-manager";
import { mapAccessControlUuidToId } from "../helpers/uuid";
import { ThemeWrapper } from "@verity/components";
import { SeverityChart, PlatformChart, StatisticsCards, ReportOverview } from "./shared/charts";
import { registerPlugin, fetchLastUpdatedDate, fetchCourseReport } from "./shared/helpers";
import { FlagLevelBadge, ReportHeader } from "./shared/report-components";
import { AssessmentTableEntry, ReportResult } from "./shared/report-types";
import SelectCourseModal from "../portal/components/select-course-modal";
import i18n from "../i18n";

function AssessmentTable({ data, hideViewReport }: { data?: AssessmentTableEntry[]; hideViewReport?: boolean }) {
	const { navigateToPage, changePortalContext } = usePortalStateContext();
	const [assessmentSearch, setAssessmentSearch] = useState<string>();
	const controlId = useId();
	const filteredData =
		data?.filter(
			(s) =>
				!assessmentSearch ||
				assessmentSearch === "" ||
				s.quizName.toLowerCase().includes(assessmentSearch.toLowerCase()),
		) ?? [];
	const columns: CoreTableColumn<AssessmentTableEntry>[] = [
		{
			header: i18n.t("instructor.components.reports.course.assessmentName"),
			key: "assessmentName",
			cell: (row) => row.data.quizName,
			truncateString: true,
			align: "left",
			width: hideViewReport ? "45%" : "37%",
			descendingComparator: (a, b) => a.quizName.localeCompare(b.quizName),
		},
		{
			header: i18n.t("instructor.components.reports.course.testTakersProctored"),
			key: "studentsProctored",
			cell: (row) => row.data.studentCount,
			align: "left",
			width: "15%",
			descendingComparator: (a, b) => a.studentCount - b.studentCount,
		},
		{
			header: i18n.t("instructor.components.reports.course.flaggedEvents"),
			key: "flaggedEvents",
			cell: (row) => row.data.flaggedEvents,
			align: "left",
			width: "15%",
			descendingComparator: (a, b) => a.flaggedEvents - b.flaggedEvents,
		},
		{
			header: i18n.t("instructor.components.reports.course.flagLevel"),
			key: "severityLevel",
			cell: (row) => <FlagLevelBadge flagLevel={row.data.severityLevel} />,
			align: "left",
			width: "18%",
			descendingComparator: (a, b) => b.severityLevel - a.severityLevel,
		},
		...(hideViewReport
			? []
			: [
					{
						header: i18n.t("instructor.components.reports.course.viewReport"),
						key: "viewReport",
						cell: (row) => (
							<CoreButton
								sx={{ marginRight: "8px" }}
								aria-label={i18n.t("instructor.components.reports.course.viewReportAriaLabel", {
									quizName: row.data.quizName,
								})}
								onClick={async () => {
									await changePortalContext({ assessmentId: row.data.quizId });
									navigateToPage("assessmentReport");
								}}
							>
								{i18n.t("instructor.components.reports.course.viewReport")}
							</CoreButton>
						),
						align: "left",
						minWidth: "200px",
					} as CoreTableColumn<AssessmentTableEntry>,
				]),
	];
	return (
		<Box display="flex" gap="14px" flexDirection="column" marginY="14px">
			<CoreSearchFieldPaper>
				<CoreSearchField
					label={i18n.t("instructor.components.reports.course.searchByAssessmentName")}
					onSearch={setAssessmentSearch}
					totalResults={filteredData.length}
					ariaControls={controlId}
					showSearchButton
					sx={{
						flexGrow: "0.7",
					}}
					value={assessmentSearch ?? ""}
				/>
			</CoreSearchFieldPaper>
			<CoreTable
				id={controlId}
				loading={data ? false : true}
				columns={columns}
				data={filteredData}
				paginationSize="large"
				rowsPerPage={10}
				noResultsString={i18n.t("instructor.components.reports.course.noResultsFound")}
			/>
		</Box>
	);
}

function DonutChartContainer({
	severityCounts,
	platformCounts,
}: {
	severityCounts?: { none: number; minor: number; major: number; severe: number };
	platformCounts?: { noneCount: number; lockdownCount: number; mobileCount: number; extensionCount: number };
}) {
	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: {
					md: "repeat(1, 1fr)",
					lg: "repeat(2, 1fr)",
				},
				gridTemplateRows: "auto",
				columnGap: "14px",
				rowGap: "15px",
			}}
		>
			<SeverityChart severityCounts={severityCounts} />
			<PlatformChart platformCounts={platformCounts} />
		</Box>
	);
}

function AssessmentTableContainer({
	data,
	hideViewReport,
}: {
	data?: AssessmentTableEntry[];
	hideViewReport?: boolean;
}) {
	return (
		<CoreCard useHeader title={i18n.t("instructor.components.reports.course.assessmentTableHeader")} icon={CourseIcon}>
			<div
				style={{
					fontSize: "15px",
					fontWeight: 400,
					lineHeight: "17.9px",
					textAlign: "left",
					color: "#6B6B6B",
					marginTop: "10px",
					marginBottom: "20px",
				}}
			>
				{i18n.t("instructor.components.reports.course.assessmentTableDescription")}
			</div>
			<AssessmentTable data={data} hideViewReport={hideViewReport} />
		</CoreCard>
	);
}

export function CourseReport({
	instituteId,
	lmsCourseId,
	courseId,
	reportDate,
	hideViewReport,
}: {
	instituteId?: number;
	lmsCourseId?: string | null;
	courseId?: number;
	reportDate?: Date;
	hideViewReport?: boolean;
}) {
	const portalAuthenticationManager = useContext(PortalAuthenticationManagerContext);
	const verityFetch = (input: RequestInfo | URL, init?: RequestInit | undefined) =>
		portalAuthenticationManager?.fetch ? portalAuthenticationManager.fetch(input, init) : fetch(input, init);
	const { reportContext, changePortalContext } = usePortalStateContext();
	const [reportData, setReportData] = useState<ReportResult>();
	const [lastUpdatedDate, setLastUpdatedDate] = useState<Date>();
	const [refreshReportModalOpen, setRefreshReportModalOpen] = useState(false);
	const [selectCourseModalOpen, setSelectCourseModalOpen] = useState(false);
	const [showRefresh, setShowRefresh] = useState(false);
	const [liveEnabled, setLiveEnabled] = useState<boolean>();
	const [courseInfo, setCourseInfo] = useState<{
		id: number;
		name?: string | null | undefined;
		code?: string | null | undefined;
		courseId?: string | null | undefined;
	}>();
	const [startDate, setStartDate] = useState(() => {
		if (reportDate) return reportDate;
		const date = new Date();
		date.setFullYear(date.getFullYear() - 1);
		date.setDate(1);
		date.setHours(0, 0, 0, 0);
		return date;
	});

	registerPlugin();

	const [endDate, setEndDate] = useState(() => {
		if (reportDate) {
			const date = new Date(reportDate);
			date.setDate(date.getDate() + 1);
			return date;
		} else {
			const date = new Date();
			const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
			lastDayOfMonth.setHours(23, 59, 59, 999);
			return lastDayOfMonth;
		}
	});

	useEffect(() => {
		const fetchCourseInfoWithId = async () => {
			if (reportContext.courseId === undefined && courseId === undefined) return;
			const courseInfo = await apiClient.course.getCourseInfoFromId(reportContext.courseId ?? courseId!);
			setCourseInfo(courseInfo);
		};
		const fetchCourseInfoLmsCourseId = async () => {
			if (!instituteId || !lmsCourseId) {
				console.error("instituteId or lmsCourseId is missing");
				return;
			}
			const courseInfo = await apiClient.course.getCourseInfoFromLmsCourseId({
				lmsCourseId: lmsCourseId,
				instituteId,
			});
			setCourseInfo(courseInfo);
		};

		reportContext.courseId || courseId
			? fetchCourseInfoWithId().catch(console.error)
			: fetchCourseInfoLmsCourseId().catch(console.error);
	}, [instituteId, lmsCourseId, reportContext.courseId, courseId]);

	useEffect(() => {
		setReportData(undefined);
		const fetchReport = async () => {
			if (!courseInfo?.id || startDate > endDate) return;
			const courseId = courseInfo.id.toString();

			const report = await fetchCourseReport({ courseId, startDate, endDate, verityFetch });

			if (report) {
				setReportData(report);
			} else {
				coreToast.info(i18n.t("instructor.components.reports.course.fetchReportToast"));
			}
		};
		fetchReport().catch(console.error);
	}, [courseInfo, startDate, endDate]);

	useEffect(() => {
		apiClient.institute
			.isLiveEnabled()
			.then((isLiveEnabled) => {
				setLiveEnabled(isLiveEnabled);
			})
			.catch(console.error);
		if (!instituteId) return;
		fetchLastUpdatedDate(setLastUpdatedDate, verityFetch, instituteId, setShowRefresh).catch(console.error);
	}, []);

	const courseName = courseInfo?.name ?? i18n.t("instructor.components.reports.course.unknownCourse");
	const courseHeaderBody = courseInfo ? (
		<Typography fontWeight={800} fontSize={"15px"} color={"#0b6f85"}>
			{courseName + " (" + courseInfo.courseId + ")"}
		</Typography>
	) : (
		<Skeleton variant="text" width={200} />
	);

	return (
		<div
			style={{
				width: "100%",
				height: "100%",
				background: "#F7F8FA",
				overflow: "auto",
			}}
		>
			<div
				style={{
					width: "100%",
					maxWidth: "100%",
					height: "auto",
					minHeight: "100%",
					margin: "0 auto",
				}}
			>
				<ThemeWrapper scoped={false}>
					<Box
						sx={{
							"&:not(:empty)": {
								display: "flex",
								width: "100%",
								maxWidth: "100%",
								padding: "10px",
								paddingTop: "0px",
								marginTop: "10px",
								boxSizing: "border-box",
							},
						}}
					>
						<VisualizerBanner />
					</Box>
					<Box display="flex" flexDirection="column" margin="10px" gap="14px" width="calc(100% - 20px)">
						<ReportHeader
							setRefreshReportModalOpen={() => setRefreshReportModalOpen(true)}
							startDate={startDate}
							endDate={endDate}
							setStartDate={setStartDate}
							setEndDate={setEndDate}
							type="course"
							lastUpdatedDate={lastUpdatedDate}
							showRefreshButton={showRefresh}
							body={courseHeaderBody}
							fixedDate={reportDate ? true : false}
							reportInfo={{
								reportType: "course",
								institutionCourseReportInfo: {
									id: courseInfo?.id,
									startDate: startDate,
									endDate: endDate,
									verityFetch: verityFetch,
									courseInfo: { courseName: courseName },
								},
							}}
							switchButton={{
								text: i18n.t("instructor.components.reports.course.switchCourse"),
								onClick: () => setSelectCourseModalOpen(true),
							}}
						/>
						<StatisticsCards
							assessments={reportData?.assessments}
							liveSessions={reportData?.liveSessions}
							proctoringHours={reportData?.proctoringHours}
							liveHours={reportData?.liveHours}
							students={reportData?.students}
							veritySessions={reportData?.veritySessions}
							averageSessions={reportData?.averageSessions}
							averageHours={reportData?.averageHours}
							endDate={endDate}
							liveEnabled={liveEnabled}
						/>
						<ReportOverview
							type="course"
							totalVeritySessions={reportData?.monthlyReport?.totalVeritySessions}
							totalLiveSessions={reportData?.monthlyReport?.totalLiveSessions}
							totalLiveCheckInSessions={reportData?.monthlyReport?.totalLiveCheckInSessions}
							totalNonLiveSessions={reportData?.monthlyReport?.totalNonLiveSessions}
							startDateFilter={startDate}
							endDateFilter={endDate}
						/>
						<DonutChartContainer
							severityCounts={reportData?.severityCounts}
							platformCounts={reportData?.platformCounts}
						/>
						<AssessmentTableContainer data={reportData?.assessmentTableReport} hideViewReport={hideViewReport} />
						{instituteId && (
							<RefreshReportModal
								instituteId={instituteId}
								handleClose={() => {
									setRefreshReportModalOpen(false);
								}}
								onSubmit={() => {
									setShowRefresh(false);
								}}
								verityFetch={verityFetch}
								open={refreshReportModalOpen}
							/>
						)}

						{selectCourseModalOpen && (
							<SelectCourseModal
								onClose={() => setSelectCourseModalOpen(false)}
								onSelect={(courseId) => changePortalContext({ courseId, assessmentId: undefined })}
								sortById
							/>
						)}
					</Box>
				</ThemeWrapper>
			</div>
		</div>
	);
}

export function convertDayOfStringToDateInterval(dayOf: string | undefined): { start: Date; end: Date } | undefined {
	if (!dayOf) return;
	const start = new Date(Number(dayOf));
	const end = new Date(start.getTime());
	end.setDate(end.getDate() + 1);
	return { start, end };
}

const portalAuthenticationManager = createPortalAuthenticationManager();
const container = document.getElementById("instructor-report-target");
if (container) {
	const root = createRoot(container);
	handleAuthenticatedTabOpen()
		.then(() => portalAuthenticationManager.init())
		.then(() => mapAccessControlUuidToId({ instituteIdParam: "institute" }))
		.catch((err) => {
			console.error(err);
		})
		.then(() => {
			const params = new URLSearchParams(window.location.search);
			const dayOf = params.get("dayof") ?? undefined;
			const courseId = Number(params.get("courseId"));
			const instituteId = Number(params.get("institute"));
			const lmsProvidedCourseId = params.get("lmsProvidedCourseId");
			let courseQuery;
			if (Number.isInteger(courseId) && Number.isInteger(instituteId)) {
				courseQuery = {
					courseId,
					instituteId: instituteId,
				};
			}
			if (Number.isInteger(instituteId) && lmsProvidedCourseId) {
				courseQuery = {
					instituteId,
					lmsProvidedCourseId,
				};
			}
			return {
				dayOf,
				courseQuery,
			};
		})
		.then(({ dayOf, courseQuery }) => {
			let reportDate;
			if (dayOf) {
				reportDate = new Date(Number(dayOf));
			}
			root.render(
				<PortalAuthenticationManagerContext.Provider value={portalAuthenticationManager}>
					<CourseReport
						instituteId={courseQuery?.instituteId}
						lmsCourseId={courseQuery?.lmsProvidedCourseId}
						courseId={courseQuery?.courseId}
						reportDate={reportDate}
						hideViewReport
					/>
					<CoreToastContainer />
				</PortalAuthenticationManagerContext.Provider>,
			);
		})
		.catch(console.error);
}